/**
 * Boot phase reporting for a progress indicator in the app shell.
 *
 * The shell paints with the HTML and then waits out auth, the boot documents and the UI corpora
 * before the first layout appears. Only this library knows when each of those finishes, and the
 * shell cannot be reached from here without pulling the DOM into a module that also runs under
 * Node - so phases are handed out through a callback the embedder supplies.
 *
 * The bundle *fetches* are deliberately not phases. They are already in flight from the HTML
 * preload hints by the time this library asks for them, so the fetch resolving says nothing about
 * when the download finished; the shell reads that from resource timing instead.
 */
const CAN_MARK = typeof performance !== 'undefined' && typeof performance.mark === 'function'

/** Prefix of the User Timing marks, so a measurement harness can pick the phases out by name. */
export const BOOT_MARK_PREFIX = 'ae-boot-'

export type BootPhase =
  | 'features'
  | 'auth'
  | 'boot-documents'
  | 'import-layouts'
  | 'import-designs'
  | 'import-controls'
  | 'lookup-tables'
  | 'booted'
  | 'rendered'

export type BootProgressCallback = (phase: BootPhase) => void
export type BootProgressReporter = (phase: BootPhase) => void

export function createBootProgressReporter(callback?: BootProgressCallback): BootProgressReporter {
  return (phase: BootPhase) => {
    if (CAN_MARK) {
      try {
        performance.mark(`${BOOT_MARK_PREFIX}${phase}`)
      } catch {
        // Never let instrumentation break booting.
      }
    }

    if (!callback) {
      return
    }

    try {
      callback(phase)
    } catch {
      // A progress indicator is not worth failing a boot over.
    }
  }
}
